import {
  CreateAnnouncementReplyResponse,
  type CreateAnnouncementRequest,
  CreateAnnouncementResponse,
} from "@edu/shared";
import { call, callVoid } from "../../lib/api.ts";

/**
 * Четыре пишущие ручки рассылки: отправить, ответить и убрать то и другое.
 *
 * ⚠️ **Читающих здесь больше нет** (правка 30.08.2026). Лента
 * (`GET /v1/announcements`), карточка (`GET /v1/announcements/:id`) и квитанция
 * на объявление (`POST /v1/announcements/:id/seen`) сняты вместе с экранами,
 * которые на них стояли: читается рассылка окном переписки
 * (`conversations-api.ts`), а квитанцию двигает открытие окна. Оставленная ручка
 * без читателя это приглашение написать на неё второй экран.
 *
 * Живут они здесь, а не в `lib/api.ts`, по той же соседской причине, что панель
 * «Где дыры» (`features/students/student-skills-api.ts`) и справочник тем формы
 * выдачи: схемы нужны ровно этим экранам. Базовый адрес, cookie и разбор отказов
 * при этом общие, поэтому наружу отсюда уезжает только `call`, а второго
 * HTTP-клиента не заводится.
 *
 * ⚠️ **Ни одна ручка не принимает и не отдаёт роль читателя.** Сторона
 * вычисляется сервером позиционно, а правила видимости живут в
 * `apps/api/src/repo/`. Клиент их не повторяет и повторять не должен: второе
 * место, где решают, кто что видит, однажды поправят не так.
 */

/**
 * Отправить объявление.
 *
 * ⚠️ Адресация ровно одна из двух: `groupId` ЛИБО `studentUserIds`. Оба поля
 * сразу это отказ `invalid_body` от `refine` в схеме, а не выбор одного из них,
 * поэтому форма собирает тело по выбранному способу, а не докладывает всё, что
 * знает.
 */
export function createAnnouncement(
  body: CreateAnnouncementRequest,
): Promise<CreateAnnouncementResponse> {
  return call("/v1/announcements", { method: "POST", body }, CreateAnnouncementResponse);
}

/**
 * Дописать в разговор.
 *
 * ⚠️ **Адресуется ОБЪЯВЛЕНИЕМ, а не разговором, и это не пережиток.** Ответ в
 * схеме прикреплён к объявлению, и куда именно писать, называет сервер полем
 * `replyToId` у страницы окна: при длинной переписке свежее объявление лежит за
 * страницей, ниже последних ответов, и экран сам его не выведет.
 */
export function addAnnouncementReply(
  announcementId: string,
  body: string,
): Promise<CreateAnnouncementReplyResponse> {
  return call(
    `/v1/announcements/${announcementId}/replies`,
    { method: "POST", body: { body } },
    CreateAnnouncementReplyResponse,
  );
}

/**
 * Убрать объявление целиком. Только у того, кто его отправил.
 *
 * ⚠️ Уносит с собой и ответы под ним, у всех, кому оно пришло. Экран называет
 * это словами до нажатия, а не после.
 */
export function deleteAnnouncement(announcementId: string): Promise<void> {
  return callVoid(`/v1/announcements/${announcementId}`, { method: "DELETE" });
}

/**
 * Убрать один ответ. Преподаватель любой, ученик свой.
 *
 * ⚠️ Отвязанному ученику эта ручка доступна, в отличие от `addAnnouncementReply`:
 * писать он перестаёт сразу, а снять свои слова с глаз остальных детей может
 * всегда. Экран поэтому не выключает кнопку по состоянию пары, и своей проверки
 * у него нет.
 */
export function deleteAnnouncementReply(announcementId: string, replyId: string): Promise<void> {
  return callVoid(`/v1/announcements/${announcementId}/replies/${replyId}`, { method: "DELETE" });
}

/*
 * Списка групп здесь нет: его читает один преподавательский экран, и вызов
 * живёт рядом с ним, в `teacher/groups-api.ts`.
 *
 * ⚠️ Прежде здесь было написано, что иначе схема группы уехала бы в стартовый
 * чанк ученику. Это неправда, и проверено ревью 29.08.2026: `@edu/shared`
 * тянет `groups.ts` в стартовый чанк сам, потому что `announcements.ts`
 * импортирует оттуда `GroupId` и `MAILING_LIST_MEMBERS_MAX` значением. Пока эти
 * два имени не переехали в нейтральный модуль контрактов, разделения нет ни
 * при какой раскладке файлов клиента.
 */
